<template lang="pug">
section
    .flex-row
        el-form-item.grow(:label="$t('Subject')" prop="subject")
            el-input(
                type="text"
                v-model="subject"
                maxlength="255"
                show-word-limit
                :disabled="hasBeenSent"
            )

        el-form-item.grow(v-if="marketingExtensionEnabled" :label="$t('Marketing Campaign')" prop="campaign")
            el-select.is-full-width(
                placeholder=""
                v-model="campaign"
                clearable
                :filterable="options && options.campaign.items.length > 10"
                valueKey="id"
                :disabled="hasBeenSent"
            )
                el-option(
                    v-for="campaignOption in options.campaign.items"
                    :key="campaignOption.id"
                    :label="campaignOption.name"
                    :value="campaignOption"
                )
                    .campaign-option
                        span {{ campaignOption.name }}
                        .small.grey {{ $t('Key') }}: {{ campaignOption.key }}

    el-form-item.no-margin(:label="$t('Preheader')" prop="preheader")
        .flex-row
            el-input(
                type="text"
                v-model="preheader"
                maxlength="255"
                show-word-limit
                :disabled="hasBeenSent"
            )
            .button-wrap
                el-popover(
                    placement="right"
                    width="250"
                    trigger="hover"
                )
                    template(#default)
                        span(v-html="$t('preheader-info')")
                    el-button(slot="reference" size="mini" type="link" circle) ?

        //- template(#label)
        //-     .flex-row
        //-         label {{ $t('Preheader') }}
        //-         el-popover(
        //-             placement="right"
        //-             width="250"
        //-             trigger="hover"
        //-         )
        //-             template(#default)
        //-                 span(v-html="$t('preheader-info')")
        //-             el-button(slot="reference" size="mini" type="link" circle) ?
        //- quill-editor(v-model="preheader" :options="quillConfig")

    Main(v-if="config && ! hasBeenSent" :hide="['publish-settings', 'viewport-settings']" :config="config" :section-options="sectionOptions")
    .newsletter-preview(v-if="hasBeenSent")
        .preview-header
            el-alert(type="info" :closable="false" show-icon)
                span {{ $t('Content cannot be edited after newsletter has been sent') }}
        .preview-content
            iframe(
                ref="template-preview"
                frameBorder="0"
                @load="templateLoaded"
            )
</template>

<i18n>
{
    "en": {
        "preheader-info": "A newsletter preheader is the short preview text that appears in an email inbox next to or below the subject line, providing a brief summary or sneak peek of the email's content. It's a crucial tool for increasing open rates because it can act as a secondary headline, enticing recipients to open the email. Without a customized preheader, many email clients will simply display the first line of the email's body as the preview, which is often not as compelling.",
        "Content cannot be edited after newsletter has been sent": "Content cannot be edited after newsletter has been sent"
    },
    "de": {
        "preheader-info": "Ein Newsletter-Preheader ist der kurze Vorschautext, der im E-Mail-Posteingang neben oder unter der Betreffzeile angezeigt wird und eine kurze Zusammenfassung oder einen Einblick in den Inhalt der E-Mail bietet. Er ist ein wichtiges Instrument zur Steigerung der Öffnungsraten, da er als zweite Überschrift fungieren und die Empfänger zum Öffnen der E-Mail animieren kann. Ohne einen individuell angepassten Preheader zeigen viele E-Mail-Programme einfach die erste Zeile des E-Mail-Textes als Vorschau an, was oft weniger ansprechend ist.",
        "Subject": "Betreff",
        "Preheader": "Preheader",
        "Marketing Campaign": "Marketing-Kampagne",
        "Key": "Schlüssel",
        "Content cannot be edited after newsletter has been sent": "Inhalte können nach dem Versand des Newsletters nicht mehr bearbeitet werden"
    }
}
</i18n>

<script>

import _ from 'lodash'
// import Vue from 'vue'

import quillConfig from '@/quill/config'
import { useSetupStore, useNewsletterNewsletterStore } from '@/stores'
import { checkRestrictions } from '@/helpers'

import Main from '@/components/Page/Main'

export default {
    name: 'NewsletterMain',
    data() {
        return {
            quillConfig,
        }
    },
    components: {
        Main,
    },
    mounted() {
        this.loadTemplatePreview()
    },
    watch: {
        hasBeenSent(newVal) {
            if (newVal) {
                this.$nextTick(() => {
                    this.loadTemplatePreview()
                })
            }
        }
    },
    methods: {
        loadTemplatePreview() {
            if (!this.hasBeenSent) return

            this.$nextTick(() => {
                const iframe = this.$refs['template-preview']
                if (!iframe) return

                const template = _.get(this.form, 'template', '')
                if (!template) return

                const iframeDoc = iframe.contentDocument || iframe.contentWindow.document
                iframeDoc.open()
                iframeDoc.write(template)
                iframeDoc.close()
            })
        },
        templateLoaded() {
            const iframe = this.$refs['template-preview']
            if (!iframe) return

            try {
                const iframeDoc = iframe.contentDocument || iframe.contentWindow.document
                const height = iframeDoc.body.scrollHeight
                iframe.style.height = height + 'px'
            } catch (error) {
                console.error('Error adjusting iframe height:', error)
            }
        }
    },
    computed: {
        newsletterStore() {
            return useNewsletterNewsletterStore()
        },
        form() {
            return this.newsletterStore.form
        },
        options() {
            return this.newsletterStore.options
        },
        hasBeenSent() {
            return !!_.get(this.form, 'started_sending_at')
        },
        subject: {
            get() {
                return _.get(this.form, 'subject', '')
            },
            set(value) {
                this.newsletterStore.setFormSubject(value)
            }
        },
        campaign: {
            get() {
                return _.get(this.form, 'campaign')
            },
            set(value) {
                this.newsletterStore.setFormCampaign(value)
            }
        },
        marketingExtensionEnabled() {
            return this.enabledExtensions.includes('marketing') && checkRestrictions('ex:marketing')
        },
        config() {
            return _.get(this.activeCms, 'config.template.newsletter')
        },
        sectionOptions() {
            const sections = _.get(this.config, 'sections') || []

            let options = []
            sections.forEach(section => {
                const config = _.get(this.activeCms, 'config.template.sections', [])
                    .find(item => item.key === section.key)

                const option = {...section, ...config}
                // TODO: initialize section
                options.push(option)
            })

            return options
        },
        preheader: {
            get() {
                return _.get(this.form, 'preheader', '')
            },
            set(value) {
                this.newsletterStore.setFormPreheader(value)
            }
        },
        enabledExtensions() {
            return useSetupStore().enabledExtensions;
        },
        // Member restriction is functionality, not a sold row - a shop carries it
        // without the restricted-area extension ever being attached.
        effectiveExtensions() {
            return useSetupStore().effectiveExtensions;
        },
        activeCms() {
            return useSetupStore().activeCms;
        },
    },
}
</script>

<style lang="sass" scoped>
.campaign-option
    padding-top: 4px
    padding-bottom: 4px
    line-height: 1.3
    .small
        font-size: 0.8em
    .grey
        color: #888

.page-main
    margin-top: 2rem
.el-form-item__label .flex-row
    align-items: flex-end
    justify-content: space-between
.el-form-item.no-margin::v-deep .el-form-item__label
    padding-right: 0 !important

.newsletter-preview
    margin-top: 1rem

    .preview-header
        margin-bottom: 1rem
        ::v-deep .el-alert__icon
            font-size: 18px

    .preview-content iframe
        width: 100%
        min-height: 600px
        background: white
        border: none
        border-radius: 4px
        box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1)
</style>
